<template>
    <div class="mail-list">
        <!-- Arama ve hızlı eylemler -->
        <div class="mail-list-actions-bar" :class="{ 'with-filters': !isGridMode && selectedThreadIds.length === 0 }">
            <div class="mail-list-select">
                <a-checkbox
                    :indeterminate="indeterminate"
                    :checked="checkAll"
                    @change="onCheckAllChange"
                    class="bulk-select-checkbox"
                />
            </div>

            <div class="mail-list-search">
                <div class="search-input-container">
                    <i class="material-icons search-icon">search</i>
                    <input type="text" v-model="searchText" :placeholder="$t('mail.searchEmail')" class="search-input" />
                </div>
            </div>

            <!-- Filtreler (normal mod için birleşik) -->
            <div v-if="!isGridMode && selectedThreadIds.length === 0" class="inline-filter-tabs">
                <a-tooltip :title="$t('chatWindow.all')">
                    <div
                        class="inline-filter-tab"
                        :class="{ active: activeFilter === 'all' }"
                        @click="setFilter('all')"
                    >
                        <i class="material-icons">mail</i>
                        <span class="filter-count">{{ count.all }}</span>
                    </div>
                </a-tooltip>
                <a-tooltip :title="$t('mail.unread')" v-if="mailType === 'self' && folder !== 'sent'">
                    <div
                        class="inline-filter-tab"
                        :class="{ active: activeFilter === 'unread' }"
                        @click="setFilter('unread')"
                    >
                        <i class="material-icons">mark_email_unread</i>
                        <span class="filter-count">{{ count.unread }}</span>
                    </div>
                </a-tooltip>
                <a-tooltip :title="$t('mail.unreply')" v-if="mailType === 'self' && folder !== 'sent'">
                    <div
                        class="inline-filter-tab"
                        :class="{ active: activeFilter === 'unanswered' }"
                        @click="setFilter('unanswered')"
                    >
                        <i class="material-icons">question_answer</i>
                        <span class="filter-count">{{ count.unanswered }}</span>
                    </div>
                </a-tooltip>
            </div>

            <div class="mail-list-tools">
                <button class="refresh-button" :class="{ rotating: loading }" @click="refreshList" :disabled="loading">
                    <i class="material-icons">refresh</i>
                </button>
            </div>
        </div>

        <!-- Filtreler ve toplu işlemler (sadece grid modda ayrı göster) -->
        <div v-if="isGridMode" class="mail-list-filters">
            <div v-if="selectedThreadIds.length === 0" class="filter-tabs">
                <a-tooltip :title="$t('chatWindow.all')">
                    <div class="filter-tab" :class="{ active: activeFilter === 'all' }" @click="setFilter('all')">
                        <i class="material-icons">mail</i>
                        <span class="filter-label">{{ $t("chatWindow.all") }}</span>
                        <span class="filter-count">{{ count.all }}</span>
                    </div>
                </a-tooltip>
                <a-tooltip :title="$t('mail.unread')" v-if="mailType === 'self' && folder !== 'sent'">
                    <div class="filter-tab" :class="{ active: activeFilter === 'unread' }" @click="setFilter('unread')">
                        <i class="material-icons"  style="margin-left: 10px;">mark_email_unread</i>
                        <span class="filter-label">{{ $t('mail.unread') }}</span>
                        <span class="filter-count">{{ count.unread }}</span>
                    </div>
                </a-tooltip>
                <a-tooltip :title="$t('mail.unreply')" v-if="mailType === 'self' && folder !== 'sent'">
                    <div
                        class="filter-tab"
                        :class="{ active: activeFilter === 'unanswered' }"
                        @click="setFilter('unanswered')"
                    >
                        <i class="material-icons"  style="margin-left: 10px;">question_answer</i>
                        <span class="filter-label">{{ $t('mail.unreply') }}</span>
                        <span class="filter-count">{{ count.unanswered }}</span>
                    </div>
                </a-tooltip>
            </div>

            <div v-if="selectedThreadIds.length > 0" class="bulk-actions">
                <button class="bulk-action-button delete-button" @click="handleDelete">
                    <i class="material-icons">delete</i>
                    <span>{{ $t("ticket.delete") }}</span>
                </button>

                <button class="bulk-action-button read-button" @click="handleMarkRead">
                    <i class="material-icons">mark_email_read</i>
                    <span>{{ $t("form.read") }}</span>
                </button>

                <div class="selected-count">{{ selectedThreadIds.length }} mail seçildi</div>
            </div>
        </div>

        <!-- Normal modda toplu işlemler çubuğu -->
        <div v-if="!isGridMode && selectedThreadIds.length > 0" class="mail-list-filters">
            <div class="bulk-actions">
                <button class="bulk-action-button delete-button" @click="handleDelete">
                    <i class="material-icons">delete</i>
                    <span>{{ $t("ticket.delete") }}</span>
                </button>

                <button class="bulk-action-button read-button" @click="handleMarkRead">
                    <i class="material-icons">mark_email_read</i>
                    <span>{{ $t("form.read") }}</span>
                </button>

                <div class="selected-count">{{ $t("mail.selectedMail", { count: selectedThreadIds.length }) }}</div>
            </div>
        </div>

        <!-- Mail listesi -->
        <div class="mail-items-container">
            <div v-if="loading" class="mail-loading-indicator">
                <div class="mail-loading-bar"></div>
            </div>
            <a-checkbox-group v-model:value="selectedThreadIds" class="mail-items-list">
                <div
                    v-for="thread in threads"
                    :key="thread._id"
                    class="mail-item"
                    :class="{
                        unread: !thread.isRead,
                        active: selectedThreadId === thread._id,
                    }"
                    @click="selectThread(thread)"
                >
                    <div class="mail-item-checkbox">
                        <a-checkbox :value="thread._id" class="bulk-select-checkbox" @click.stop></a-checkbox>
                    </div>

                    <div class="mail-item-content">
                        <div class="mail-item-header">
                            <div class="sender-name">{{ getParticipants(thread) }}</div>
                            <div class="mail-date">{{ formatDate(thread.createdAt) }}</div>
                        </div>

                        <div class="mail-item-subject">
                            <span class="subject-text">{{ thread.subject }}</span>
                            <span class="attachment-icon" v-if="thread.attachments && thread.attachments.length">
                                <i class="material-icons">attachment</i>
                            </span>
                        </div>

                        <div class="mail-item-preview">{{ getPreviewText(thread.content) }}</div>
                    </div>
                </div>

                <div v-if="threads.length === 0" class="no-mails-message">
                    <div class="empty-icon">
                        <i class="material-icons">mail</i>
                    </div>
                    <p>{{ $t("mail.noMailFound") }}</p>
                </div>

                <!-- Daha Fazla Butonu -->
                <div v-if="showLoadMoreButton" class="load-more-container">
                    <button class="load-more-button" @click="loadMoreMails" :disabled="loading">
                        <i class="material-icons" v-if="loading">sync</i>
                        <span>{{ loading ? $t("message.loading") : $t("billing.more") }}</span>
                    </button>
                </div>
            </a-checkbox-group>
        </div>
    </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import _ from "lodash";
import { MAIL_API } from "../../../../../api/mail";

export default {
    name: "MailList",
    components: {},
    props: {
        selectedThreadId: {
            type: String,
            default: null,
        },
        refVisitorId: {
            type: String,
            default: null,
        },
        folder: {
            type: String,
            required: true,
        },
        mailType: {
            type: String,
            default: "self",
        },
        isGridMode: {
            type: Boolean,
            default: true,
        },
        singleMode: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            loading: false,
            selectedThreadIds: [],
            searchText: "",
            activeFilter: "all",
            indeterminate: false,
            checkAll: false,
            folderNames: {
                inbox: "Gelen Kutusu",
                sent: "Gönderilen",
            },
            cancels: [],
            threads: [],
            paginationCursor: {
                skip: 0,
                limit: 20,
            },
            count: {
                all: 0,
                unread: 0,
                unanswered: 0,
            },
            debouncedSearch: null,
        };
    },
    mounted() {
        this.fetchMails();
        this.debouncedSearch = _.debounce(this.fetchMails, 500);
    },
    computed: {
        ...mapState({
            user: (state) => state.auth.user,
        }),
        selectedEmail() {
            return this.$parent.selectedEmail;
        },
        folderName() {
            return this.folderNames[this.folder] || "Mailler";
        },
        showLoadMoreButton() {
            return this.threads.length >= 20;
        },
    },
    watch: {
        selectedEmail: {
            deep: true,
            handler(newVal, oldVal) {
                if (newVal && newVal !== oldVal) {
                    this.activeFilter = "all";
                    this.fetchMails();
                }
            },
        },
        searchText() {
            this.debouncedSearch();
        },
        selectedThreadIds(val) {
            if (this.threads && this.threads.length > 0) {
                this.indeterminate = !!val.length && val.length < this.threads.length;
                this.checkAll = val.length === this.threads.length && this.threads.length > 0;
            }
        },
        folder() {
            this.activeFilter = "all";
            this.fetchMails();
        },
        activeFilter() {
            this.fetchMails();
        },
    },
    methods: {
        ...mapActions({
            cleanUnreadThread: "mail/cleanUnreadThread",
        }),
        selectThread(thread) {
            if (!thread.isRead) {
                this.cleanUnreadThread({
                    threadId: thread.threadId,
                    userId: this.user._id,
                }).then(() => {
                    this.threads = this.threads.map((t) => {
                        t.isRead = t._id === thread._id ? true : t.isRead;
                        return t;
                    });
                    if (this.count.unread) this.count.unread = this.count.unread - 1;
                });
            }
            this.$emit("thread-selected", thread);
        },
        refreshList() {
            this.fetchMails();
        },
        handleDelete() {
            if (this.selectedThreadIds.length > 0) {
                MAIL_API.deleteMultiThreads(this.selectedThreadIds).then(() => {
                    this.refreshList();
                });
                this.selectedThreadIds = [];
            }
        },
        handleMarkRead() {
            if (this.selectedThreadIds.length > 0) {
                MAIL_API.markReadMulti(this.selectedThreadIds).then(() => {
                    this.refreshList();
                });
                this.selectedThreadIds = [];
            }
        },
        setFilter(filter) {
            this.activeFilter = filter;
        },
        onCheckAllChange(e) {
            this.selectedThreadIds = e.target.checked ? this.threads.map((thread) => thread._id) : [];
        },
        getParticipants(thread) {
            const participants = thread.participants.filter(Boolean).map((email) => {
                return email === this.selectedEmail.mail
                    ? this.$t("chatWindow.me")
                    : email.split("@")[0];
            });

            if (participants.length > 3) {
                const remainingCount = participants.length - 3;
                return this.$t("mail.participantList", {
                    first: participants[0],
                    second: participants[1],
                    third: participants[2],
                    remaining: remainingCount
                });
            } else {
                return participants.join(", ");
            }
        },
        formatDate(timestamp) {
            const date = new Date(timestamp);
            const now = new Date();
            const diff = now - date;
            const oneDay = 24 * 60 * 60 * 1000;

            if (diff < oneDay) {
                return this.$moment(date).format("HH:mm");
            } else if (diff < 7 * oneDay) {
                return this.$moment(date).format("ddd");
            } else {
                return this.$moment(date).format("DD MMM");
            }
        },
        getPreviewText(html) {
            const div = document.createElement("div");
            div.innerHTML = html;
            return div.textContent || div.innerText || "";
        },

        async getUnReplyMails() {
            const mailAddress = this.selectedEmail.mail;
            return await MAIL_API.getUnReplyMails({
                mailAddress,
                incoming: this.folder === "inbox",
                skip: this.paginationCursor.skip,
                limit: this.paginationCursor.limit,
                searchValue: this.searchText,
                refVisitorId: this.refVisitorId,
            });
        },

        async getUnreadMails() {
            const mailAddress = this.selectedEmail.mail;
            return await MAIL_API.getUnreadMails({
                mailAddress,
                incoming: this.folder === "inbox",
                skip: this.paginationCursor.skip,
                limit: this.paginationCursor.limit,
                searchValue: this.searchText,
                refVisitorId: this.refVisitorId,
            });
        },

        async getAllMails() {
            const mailAddress = this.selectedEmail.mail;
            const res = await MAIL_API.getMailList({
                mailAddress,
                incoming: this.mailType !== 'self' || this.folder === "inbox",
                outgoing: this.mailType !== 'self' || this.folder === "sent",
                skip: this.paginationCursor.skip,
                limit: this.paginationCursor.limit,
                searchValue: this.searchText,
                refVisitorId: this.refVisitorId,
            });
            return res;
        },

        async fetchMails(concat = false) {
            if (!this.selectedEmail) {
                return;
            }
            if (!concat) {
                this.threads = [];
                this.paginationCursor.skip = 0;
            }
            this.loading = true;
            let mails = [];
            if (this.activeFilter === "all") {
                const data = await this.getAllMails();
                mails = concat ? [...this.threads, ...data.mails] : data.mails;
                this.count.all = data.count === undefined ? this.count.all : data.count;
                this.count.unread = data.unreadCount === undefined ? this.count.unread : data.unreadCount;
                this.count.unanswered = data.unReplyCount === undefined ? this.count.unanswered : data.unReplyCount;
            } else if (this.activeFilter === "unread") {
                const data = await this.getUnreadMails();
                this.count.unread = data.count === undefined ? this.count.unread : data.count;
                mails = concat ? [...this.threads, ...data.mails] : data.mails;
            } else if (this.activeFilter === "unanswered") {
                const data = await this.getUnReplyMails();
                this.count.unanswered = data.count === undefined ? this.count.unanswered : data.count;
                mails = concat ? [...this.threads, ...data.mails] : data.mails;
            }
            this.threads = mails;
            this.loading = false;
        },
        loadMoreMails() {
            this.paginationCursor.skip += 20;
            this.fetchMails(true);
        },
    },
    beforeDestroy() {
        this.cancels.forEach((cancel) => cancel());
    },
};
</script>

<style lang="scss">
.bulk-select-checkbox {
    .ant-checkbox-checked .ant-checkbox-inner {
        background-color: #22272e;
        border-color: #22272e;

        &:hover {
            border-color: #22272e;
        }
    }

    .ant-checkbox-indeterminate .ant-checkbox-inner::after {
        background-color: #22272e;
    }
}
</style>

<style lang="scss" scoped>
.mail-list {
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
    border-right: 1px solid #eaeaea;
    background-color: #fff;

    .mail-list-actions-bar {
        display: flex;
        align-items: center;
        padding: 14px 16px;
        border-bottom: 1px solid #eaeaea;

        &.with-filters {
            flex-wrap: wrap;
            gap: 8px;
        }

        .mail-list-select {
            margin-right: 12px;

            .bulk-select-checkbox {
                transform: scale(1.15);
            }
        }

        .mail-list-search {
            flex: 1;
            min-width: 180px;

            .search-input-container {
                position: relative;
                width: 100%;

                .search-icon {
                    position: absolute;
                    left: 10px;
                    top: 50%;
                    transform: translateY(-50%);
                    color: #888;
                    font-size: 18px;
                }

                .search-input {
                    width: 100%;
                    height: 36px;
                    border-radius: 6px;
                    border: 1px solid #d9d9d9;
                    padding: 8px 12px 8px 36px;
                    font-size: 14px;
                    color: #333;
                    transition: all 0.3s;

                    &:hover {
                        border-color: #22272d;
                    }

                    &:focus {
                        border-color: #22272d;
                        outline: none;
                        box-shadow: 0 0 0 2px rgba(34, 39, 45, 0.1);
                    }

                    &::placeholder {
                        color: #aaa;
                    }
                }
            }
        }

        .inline-filter-tabs {
            display: flex;
            gap: 10px;
            margin-left: 10px;

            .inline-filter-tab {
                position: relative;
                display: flex;
                align-items: center;
                justify-content: center;
                width: 36px;
                height: 36px;
                border-radius: 6px;
                background-color: #f5f5f5;
                cursor: pointer;
                transition: all 0.2s;

                i {
                    font-size: 20px;
                    color: #666;
                }

                .filter-count {
                    position: absolute;
                    top: -5px;
                    right: -5px;
                    min-width: 18px;
                    height: 18px;
                    border-radius: 9px;
                    background-color: #666;
                    color: #fff;
                    font-size: 10px;
                    font-weight: 600;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 0 5px;
                }

                &:hover {
                    background-color: #e8e8e8;
                }

                &.active {
                    background-color: #22272d;

                    i {
                        color: #fff;
                    }

                    .filter-count {
                        background-color: #fff;
                        color: #22272d;
                    }
                }
            }
        }

        .mail-list-tools {
            margin-left: 12px;

            .refresh-button {
                width: 36px;
                height: 36px;
                border-radius: 6px;
                display: flex;
                align-items: center;
                justify-content: center;
                background-color: #f5f5f5;
                border: 1px solid #eaeaea;
                cursor: pointer;
                transition: all 0.2s;

                i {
                    font-size: 18px;
                    color: #555;
                }

                &:hover {
                    background-color: #e8e8e8;
                }

                &:disabled {
                    cursor: not-allowed;
                    opacity: 0.6;
                }

                &.rotating i {
                    animation: rotate 1.2s linear infinite;
                }
            }
        }
    }

    .mail-list-filters {
        padding: 10px 16px;
        border-bottom: 1px solid #eaeaea;

        .filter-tabs {
            display: flex;
            width: 100%;
            height: 40px;
            border-radius: 8px;
            background-color: #f5f5f5;
            padding: 4px;

            .filter-tab {
                flex: 1;
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 6px;
                cursor: pointer;
                font-size: 13px;
                color: #555;
                position: relative;
                border-radius: 6px;
                transition: all 0.2s;
                overflow: hidden;

                i {
                    font-size: 16px;
                    min-width: 16px;
                }

                .filter-label {
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    max-width: 100px;
                }

                .filter-count {
                    font-size: 11px;
                    font-weight: 600;
                    color: #666;
                    background-color: #e8e8e8;
                    min-width: 18px;
                    height: 18px;
                    border-radius: 9px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 0 6px;
                }

                &:hover {
                    background-color: #ededed;
                }

                &.active {
                    background-color: #fff;
                    font-weight: 500;
                    color: #333;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);

                    .filter-count {
                        background-color: #ddd;
                        color: #333;
                    }
                }
            }
        }

        .bulk-actions {
            display: flex;
            align-items: center;
            gap: 12px;

            .bulk-action-button {
                display: flex;
                align-items: center;
                gap: 6px;
                padding: 6px 12px;
                border-radius: 6px;
                border: 1px solid #eaeaea;
                background-color: #f5f5f5;
                cursor: pointer;
                transition: all 0.2s;

                i {
                    font-size: 18px;
                }

                span {
                    font-size: 13px;
                    font-weight: 500;
                }

                &:hover {
                    background-color: #e8e8e8;
                }
            }

            .delete-button {
                color: #d32f2f;

                &:hover {
                    background-color: #ffebee;
                }
            }

            .read-button {
                color: #1976d2;

                &:hover {
                    background-color: #e3f2fd;
                }
            }

            .selected-count {
                margin-left: auto;
                font-size: 13px;
                color: #666;
                font-weight: 500;
            }
        }
    }

    .mail-items-container {
        flex: 1;
        overflow: auto;
        padding: 4px 8px;
        position: relative;

        /* Özel scrollbar stilleri */
        &::-webkit-scrollbar {
            width: 4px;
        }

        &::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 2px;
        }

        &::-webkit-scrollbar-thumb {
            background: #22272d;
            border-radius: 2px;
        }

        &::-webkit-scrollbar-thumb:hover {
            background: #1a1f23;
        }

        /* Firefox için scrollbar stilleri */
        scrollbar-width: thin;
        scrollbar-color: #22272d #f1f1f1;

        .mail-items-list {
            display: flex;
            flex-direction: column;
            width: 100%;
        }

        .load-more-container {
            display: flex;
            justify-content: center;
            padding: 20px 0;

            .load-more-button {
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 8px;
                padding: 8px 20px;
                border-radius: 6px;
                background-color: #f5f5f5;
                border: 1px solid #e0e0e0;
                color: #555;
                font-size: 14px;
                font-weight: 500;
                cursor: pointer;
                transition: all 0.2s;

                i {
                    font-size: 18px;

                    &:only-child {
                        animation: rotate 1.2s linear infinite;
                    }
                }

                &:hover {
                    background-color: #eeeeee;
                    border-color: #d5d5d5;
                }

                &:disabled {
                    opacity: 0.7;
                    cursor: not-allowed;
                }
            }
        }

        .mail-item {
            display: flex;
            padding: 12px;
            border-bottom: 1px solid #eee;
            cursor: pointer;
            border-radius: 8px;
            margin: 4px 0;
            transition: all 0.2s;

            &:hover {
                background-color: #f9f9f9;
            }

            &.unread {
                background-color: #f9f9f9;
                position: relative;

                &::before {
                    content: "";
                    position: absolute;
                    left: 0;
                    top: 50%;
                    transform: translateY(-50%);
                    width: 4px;
                    height: 20px;
                    background-color: #555;
                    border-radius: 0 4px 4px 0;
                }

                .mail-item-content {
                    .sender-name,
                    .subject-text {
                        font-weight: 600;
                        color: #22272d;
                    }
                }
            }

            &.active {
                background-color: #f2f2f2;
                box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
                position: relative;

                &::before {
                    content: "";
                    position: absolute;
                    left: 0;
                    top: 50%;
                    transform: translateY(-50%);
                    width: 4px;
                    height: 20px;
                    background-color: #22272d;
                    border-radius: 0 4px 4px 0;
                }

                .mail-item-content {
                    .sender-name,
                    .subject-text {
                        color: #22272d;
                        font-weight: 500;
                    }
                }
            }

            .mail-item-checkbox {
                margin-right: 12px;
                display: flex;
                align-items: center;
            }

            .mail-item-content {
                flex: 1;
                min-width: 0;

                .mail-item-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 4px;

                    .sender-name {
                        font-size: 14px;
                        color: #444;
                        font-weight: 500;
                        white-space: nowrap;
                        overflow: hidden;
                        text-overflow: ellipsis;
                    }

                    .mail-date {
                        font-size: 12px;
                        color: #888;
                        margin-left: 8px;
                        white-space: nowrap;
                    }
                }

                .mail-item-subject {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 4px;

                    .subject-text {
                        font-size: 14px;
                        color: #333;
                        white-space: nowrap;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        flex: 1;
                    }

                    .attachment-icon {
                        margin-left: 8px;

                        i {
                            font-size: 16px;
                            color: #888;
                        }
                    }
                }

                .mail-item-preview {
                    font-size: 13px;
                    color: #888;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
            }
        }

        .no-mails-message {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 40px 20px;

            .empty-icon {
                width: 60px;
                height: 60px;
                border-radius: 50%;
                background-color: #f5f5f5;
                display: flex;
                align-items: center;
                justify-content: center;
                margin-bottom: 16px;

                i {
                    font-size: 28px;
                    color: #999;
                }
            }

            p {
                font-size: 15px;
                color: #666;
            }
        }
    }
}

@keyframes rotate {
    from {
        transform: rotate(0deg);
    }

    to {
        transform: rotate(360deg);
    }
}

@keyframes mail-loading {
    0% {
        width: 0;
    }

    50% {
        width: 70%;
    }

    100% {
        width: 100%;
    }
}

.mail-loading-indicator {
    height: 3px;
    width: 100%;
    background-color: #f0f0f0;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    z-index: 5;
}

.mail-loading-bar {
    height: 100%;
    background-color: #22272d;
    width: 0;
    border-radius: 0 2px 2px 0;
    position: relative;
    animation: mail-loading 1.5s ease-in-out infinite;
}

// Responsive ekran boyutları için ek stiller
@media (max-width: 600px) {
    .mail-list {
        .mail-list-filters {
            .filter-tabs {
                .filter-tab {
                    .filter-label {
                        display: none;
                    }

                    i {
                        font-size: 18px;
                    }
                }
            }
        }
    }
}

@media (min-width: 601px) and (max-width: 960px) {
    .mail-list {
        .mail-list-filters {
            .filter-tabs {
                .filter-tab {
                    .filter-label {
                        max-width: 50px;
                    }
                }
            }
        }
    }
}
</style>
